import React from 'react';
import { Wholesale } from 'features/graphql';
import { PriceShowWholesaleHeaders } from './PriceShowWholesaleHeaders';

interface PriceShowTableHeaderProps {
  zoomLevel: number;
  /** 列リサイズ開始ハンドラ（状態は親が保持） */
  handleResizeStart: (e: React.MouseEvent, column: string) => void;
  wholesales: Wholesale[];
  /** 価格表更新日時（アカウント通知の生値） */
  pricelistUpdate: string | null;
  /** 「安」マーク列を表示するか（全医薬品にわたる判定のため親で算出して渡す） */
  showMinPriceColumn: boolean;
}

/**
 * 価格表の5段組みテーブルヘッダー（D4 step7: PriceShowList から thead を分離）。
 * 状態は親が保持し props で受け渡す。構造・スタイルは分離前と不変。
 */
export const PriceShowTableHeader: React.FC<PriceShowTableHeaderProps> = ({
  zoomLevel,
  handleResizeStart,
  wholesales,
  pricelistUpdate,
  showMinPriceColumn,
}) => {
  return (
    <thead className="text-success border-bottom border-success sticky-header">
      <tr>
        <th rowSpan={5} className="fixed-col-1"></th>
        <th rowSpan={5} className="align-middle fixed-col-2">
          区分
        </th>
        <th rowSpan={5} className="fixed-col-3"></th>
        <th className="fixed-col-4 fixed-column">
          <div>品名</div>
          <div
            className="column-resizer"
            onMouseDown={(e) => handleResizeStart(e, 'medicineName')}
          />
        </th>
        <th
          style={{
            width: `${6 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          年度
        </th>
        <th
          style={{
            width: `${6 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          当年度
        </th>

        {/* Dynamic wholesale headers instead of hardcoded ones */}
        <PriceShowWholesaleHeaders
          wholesales={wholesales}
          pricelistUpdate={pricelistUpdate}
          zoomLevel={zoomLevel}
          showMinPriceColumn={showMinPriceColumn}
        />
        <th
          style={{
            width: `${13 * zoomLevel}rem`,
            minWidth: `${13 * zoomLevel}rem`,
            maxWidth: `${13 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          販売開始予定日(薬価収載)
        </th>
        <th
          style={{
            width: `${13 * zoomLevel}rem`,
            minWidth: `${13 * zoomLevel}rem`,
            maxWidth: `${13 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          販売終了日(経過措置)
        </th>
        <th
          style={{
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          JANコード
        </th>
      </tr>
      <tr>
        <th className="fixed-col-4 fixed-column">
          <div>規格</div>
          <div className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 'capacity')} />
        </th>
        <th
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${8 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          開始日-終了日
        </th>
        <th
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${8 * zoomLevel}rem`,
            maxWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          開始日-終了日
        </th>
        <th
          style={{
            width: `${12 * zoomLevel}rem`,
            minWidth: `${12 * zoomLevel}rem`,
            maxWidth: `${12 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          販売開始予定日(販売開始)
        </th>
        <th
          style={{
            width: `${12 * zoomLevel}rem`,
            minWidth: `${12 * zoomLevel}rem`,
            maxWidth: `${12 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          販売終了日(販売中止)
        </th>
        <th
          style={{
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          YJコード
        </th>
      </tr>
      <tr>
        <th className="fixed-col-4 fixed-column">
          <div>一般名</div>
          <div className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 'commonName')} />
        </th>
        <th
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${5 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          薬価種別
        </th>
        <th
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${5 * zoomLevel}rem`,
            maxWidth: `${7 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          薬価種別
        </th>
        <th style={{ backgroundColor: 'white', width: 'auto' }}></th>
        <th style={{ backgroundColor: 'white', width: 'auto' }}></th>
        <th
          style={{
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          GS1コード
        </th>
      </tr>
      <tr>
        <th className="fixed-col-4 fixed-column">
          <div>薬効分類</div>
          <div
            className="column-resizer"
            onMouseDown={(e) => handleResizeStart(e, 'drugCategoryName')}
          />
        </th>
        <th
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${8 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          薬価
        </th>
        <th
          style={{
            width: `${7 * zoomLevel}rem`,
            minWidth: `${6 * zoomLevel}rem`,
            maxWidth: `${8 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          当年度薬価
        </th>
        <th
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          薬価収載年月日
        </th>
        <th
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          経過措置年月日
        </th>
        <th style={{ backgroundColor: 'white', width: 'auto' }}></th>
      </tr>
      <tr>
        <th className="fixed-col-4 fixed-column">
          <div>販売元名</div>
          <div
            className="column-resizer"
            onMouseDown={(e) => handleResizeStart(e, 'salesManufacturer')}
          />
        </th>
        <th style={{ backgroundColor: 'white', width: 'auto' }}></th>
        <th
          style={{
            width: `${6 * zoomLevel}rem`,
            minWidth: `${5 * zoomLevel}rem`,
            maxWidth: `${6 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          値引率
        </th>
        <th
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          販売開始年月日
        </th>
        <th
          style={{
            width: `${10 * zoomLevel}rem`,
            minWidth: `${10 * zoomLevel}rem`,
            backgroundColor: 'white',
          }}
        >
          販売中止年月日
        </th>
        <th style={{ backgroundColor: 'white', width: 'auto' }}></th>
      </tr>
    </thead>
  );
};
